import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../asset/project/default_1.png';
import type { SelectedData } from '../../shared/dto/EntityRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { resolveWorkTitle } from '../../shared/util/workDisplayText';
import deleteIcon from '../../asset/icon/delete.png';
import { selectedDataRowKey } from '../../shared/util/moveItemsToIndex';
import { shrinkString } from '../../shared/util/shrinkString';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import WorkArtistListLine from './WorkArtistListLine';

/** WorkListPdfModalIndexing 등에서 drop 시 getData 폴백용 */
export const WORK_LIST_PDF_INDEX_DRAG_MIME_TYPE = 'text/plain';

const HANDLE_ICON = '::';
const TITLE_MAX = 35;

interface WorkListPdfIndexingRowProps {
  item: SelectedData;
  /** 0-based; 표시는 +1 */
  orderIndex: number;
  isDragging: boolean;
  /** 드래그 시 여러 행을 한 블록으로 묶을 때 행 선택 (IndexWorkInfo 와 동일) */
  isReorderPicked?: boolean;
  onToggleReorderPick?: () => void;
  onRemoveFromSelection?: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const WorkListPdfIndexingRow: React.FC<WorkListPdfIndexingRowProps> = ({
  item,
  orderIndex,
  isDragging,
  isReorderPicked = false,
  onToggleReorderPick,
  onRemoveFromSelection,
  onDragStart,
  onDragEnd,
}) => {
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const { workPropertyVisible } = useWorkPropertyVisible();
  const engVisible = workPropertyVisible?.engVisible !== false;
  const displayTitle = useMemo(
    () =>
      resolveWorkTitle(
        { title: item.title ?? '', titleEn: item.titleEn ?? '' },
        workContentMode,
        engVisible,
      ),
    [item.title, item.titleEn, workContentMode, engVisible],
  );
  const resolvedUrl = item.originUrl || '';
  const imageUrl = useOptimizedImageUrl(resolvedUrl, imgContainerRef);

  const shrinkedTitle = useMemo(
    () => shrinkString(displayTitle, TITLE_MAX),
    [displayTitle]
  );
  const orderLabel = String(orderIndex + 1);
  const rowKey = selectedDataRowKey(item);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(WORK_LIST_PDF_INDEX_DRAG_MIME_TYPE, rowKey);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart();
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  const handleRemove = () => {
    onRemoveFromSelection?.();
  };

  return (
    <Root
      $isDragging={isDragging}
      $isReorderPicked={isReorderPicked}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onToggleReorderPick?.();
      }}
    >
      <DragBody draggable onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Column $flex="0.3" $mutedLabel $justifyContent="flex-start">
          <DragHandle>{HANDLE_ICON}</DragHandle>
          {orderLabel}
        </Column>
        <Column $flex="0.4">
          <ImgWrapper ref={imgContainerRef}>
            <ThumbImg
              draggable={false}
              src={imageUrl === '' ? defaultImg : imageUrl}
              alt={displayTitle}
            />
          </ImgWrapper>
        </Column>
        <Column $flex="2.5" $justifyContent="flex-start" $paddingLeft="10px">
          <TitleStack>
            <span>{shrinkedTitle}</span>
            <WorkArtistListLine artist={item.artist} maxLength={TITLE_MAX} />
          </TitleStack>
        </Column>
      </DragBody>
      <Column $flex="0.1" $justifyContent="center">
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={deleteIcon}
          hoverOpacity
          onClick={handleRemove}
        />
      </Column>
    </Root>
  );
};

const Root = styled.article<{ $isDragging: boolean; $isReorderPicked: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.typography.Body_03_2};
  color: ${({ theme }) => theme.colors.color_Gray_03};

  height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  min-height: ${({ $isDragging }) => ($isDragging ? '10px' : '40px')};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleY(0.2) scaleX(0.5)' : 'scaleY(1) scaleX(1)'};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  transition: height 0.4s ease-out, transform 0.4s ease-out, opacity 0.4s ease-out;
  padding-left: 4px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  background-color: ${({ $isReorderPicked, theme }) =>
    $isReorderPicked ? theme.colors.color_Gray_06 : 'transparent'};
`;

const DragBody = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0;
  cursor: grab;

  &:hover {
    transform: scale(0.98);
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  margin-right: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const TitleStack = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Column = styled.div<{
  $flex: string;
  $justifyContent?: 'center' | 'flex-start';
  $paddingLeft?: string;
  $mutedLabel?: boolean;
}>`
  flex: ${({ $flex }) => $flex};
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: ${({ $justifyContent }) => $justifyContent ?? 'center'};
  padding-left: ${({ $paddingLeft }) => $paddingLeft ?? 0};

  user-select: none;
  color: ${({ theme, $mutedLabel }) =>
    $mutedLabel ? theme.colors.color_Gray_05 : 'inherit'};
  ${({ theme }) => theme.typography.Body_04};
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 75%;
  display: flex;
  align-items: center;
  justify-content: center;

  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbImg = styled.img`
  width: 60%;
  height: 80%;

  object-fit: contain;
`;

export default WorkListPdfIndexingRow;
